import 'dotenv/config';
import ora from 'ora';
import chalk from 'chalk';
import path from 'node:path';
import prompts from 'prompts';
import { execa } from 'execa';
import template from 'lodash.template';
import { existsSync, promises as fs } from 'node:fs';

import { rawConfigSchema } from '../schema';
import {
  logger,
  templates,
  hasTailwindJsonConfig,
  getProjectPackageManager,
} from '.';
import { resolveConfigPaths } from './getMultiverseUIConfig';

const PROJECT_DEPENDENCIES = [
  'clsx',
  'react-icons',
  'tailwind-merge',
  '@tailwindcss/typography',
  'class-variance-authority',
  '@tailwindcss/container-queries',
] as const;
const V3_PROJECT_DEPENDENCIES = ['tailwindcss-animate'] as const;
const V4_PROJECT_DEPENDENCIES = ['tw-animate-css'] as const;

type PromptAnswers = {
  tailwindVersion?: 'v3' | 'v4';
  tailwindJsonConfigPath?: string;
  globalCssPath?: string;
};

const runSetupProcess = async (cwd: string) => {
  const highlight = (text: string) => chalk.cyan(text);
  const answers: PromptAnswers = {};

  answers.tailwindVersion = (
    await prompts([
      {
        type: 'select',
        name: 'tailwindVersion',
        message: `What is your tailwindcss version?`,
        choices: [
          {
            title: 'v3',
            value: 'v3',
            selected: true,
            description: '(stable)',
          },
          {
            title: 'v4',
            value: 'v4',
            description: '(experimental)',
          },
        ],
      },
    ])
  ).tailwindVersion as PromptAnswers['tailwindVersion'];

  if (answers.tailwindVersion === 'v3') {
    if (!(await hasTailwindJsonConfig(cwd))) {
      throw new Error(
        'Tailwind CSS v3 is not installed. Visit https://v3.tailwindcss.com/ to get started.'
      );
    }
    answers.tailwindJsonConfigPath = (
      await prompts([
        {
          type: 'text',
          name: 'tailwindJsonConfigPath',
          message: `Where is your ${highlight('tailwind.config.js')} located?`,
          initial: 'tailwind.config.js',
        },
      ])
    ).tailwindJsonConfigPath as PromptAnswers['tailwindJsonConfigPath'];
  }

  answers.globalCssPath = (
    await prompts([
      {
        type: 'text',
        name: 'globalCssPath',
        message: `Where is your ${highlight('global CSS')} file?`,
        initial: 'src/index.css',
      },
    ])
  ).globalCssPath as PromptAnswers['globalCssPath'];

  const options = await prompts([
    {
      type: 'text',
      name: 'components',
      message: `Where do you want your ${highlight('components')} to go by default?`,
      initial: 'src/components',
    },
    {
      type: 'text',
      name: 'utilities',
      message: `Where do you want your ${highlight('utilities.ts/js')} to go?`,
      initial: 'src',
    },
  ]);

  const rawConfig = rawConfigSchema.parse({
    $schema: `${process.env.BASE_URL}/schema.json`,
    tailwind: {
      version: answers.tailwindVersion,
      config: answers.tailwindJsonConfigPath,
      css: answers.globalCssPath,
    },
    directory: {
      utilities: options.utilities,
      components: options.components,
    },
  });

  const { proceed } = await prompts({
    type: 'confirm',
    name: 'proceed',
    message: `Write configuration to ${highlight(
      'multiverse.config.json'
    )}. Proceed?`,
    initial: true,
  });

  if (!proceed) {
    process.exit(0);
  }

  // Write to file.
  logger.info('');
  let spinner = ora(`Writing multiverse.config.json...`).start();
  const targetPath = path.resolve(cwd, 'multiverse.config.json');
  await fs.writeFile(targetPath, JSON.stringify(rawConfig, null, 2), 'utf8');
  spinner.succeed();

  // const config = await resolveConfigPaths(cwd, rawConfig);
  const config = await resolveConfigPaths(cwd, rawConfig);
  if (!config) {
    logger.error("There's a problem while writing/reading the config.");
    return;
  }

  spinner = ora(`Initializing project...`)?.start();

  // Ensure all resolved paths directories exist.
  for (const resolvedPath of Object.values(config.resolvedPaths)) {
    // Determine if the path is a file or directory.
    let dirname = path.extname(resolvedPath)
      ? path.dirname(resolvedPath)
      : resolvedPath;

    if (!existsSync(dirname)) {
      await fs.mkdir(dirname, { recursive: true });
    }
  }

  // ---------------------------------------------------------------------------
  // Support typescript projects only at the moment
  // ---------------------------------------------------------------------------
  const extension = 'ts';

  switch (answers.tailwindVersion) {
    case 'v4':
      // Write css/config file.
      await fs.writeFile(
        config.resolvedPaths.tailwindCss,
        templates.TAILWIND_CSS_V4,
        'utf8'
      );
      break;

    default:
      // Write tailwind config.
      await fs.writeFile(
        config.resolvedPaths.tailwindConfig,
        template(templates.TAILWIND_CONFIG_TS)({
          extension,
        }),
        'utf8'
      );
      // Write css file.
      await fs.writeFile(
        config.resolvedPaths.tailwindCss,
        templates.TAILWIND_CSS,
        'utf8'
      );
      break;
  }

  // Write cn file.
  await fs.writeFile(
    `${config.resolvedPaths.utilities}/utilities.${extension}`,
    templates.UTILS_TS,
    'utf8'
  );

  spinner?.succeed();

  // Install dependencies.
  const dependenciesSpinner = ora(`Installing dependencies...`)?.start();
  const packageManager = await getProjectPackageManager(cwd);

  const deps = [
    ...PROJECT_DEPENDENCIES,
    ...(answers.tailwindVersion === 'v3'
      ? V3_PROJECT_DEPENDENCIES
      : V4_PROJECT_DEPENDENCIES),
  ];

  await execa(
    packageManager,
    [packageManager === 'npm' ? 'install' : 'add', ...deps],
    {
      cwd,
    }
  );
  dependenciesSpinner?.succeed();
};

export default runSetupProcess;
